import { Award, ListCollapse, UserCheck, Users } from "lucide-react";
import { BsCalendarCheck, BsCalendarPlus } from "react-icons/bs";
import {
  FaCalendar,
  FaEnvelopeOpenText,
  FaExclamationTriangle,
  FaHome,
  FaStream,
  FaStripeS,
  FaTicketAlt,
  FaTools,
  FaUsersCog,
} from "react-icons/fa";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { IoPricetag } from "react-icons/io5";
import { MdOutlineSwapHoriz } from "react-icons/md";
import { RiLockPasswordFill, RiMailLine, RiSlideshowLine, RiVideoLine } from "react-icons/ri";

// Define interfaces for type safety
interface SubItem {
  to: string;
  icon: React.ReactNode;
  label: string;
}

interface MenuItem {
  id: number;
  title: string;
  icon: React.ReactNode;
  to?: string;
  subItems?: SubItem[];
}

// Centralized menu configuration for dashboard
export const menuConfig: MenuItem[] = [
  {
    id: 1,
    title: "Statistiques",
    icon: <i className="text-xl las la-chart-area dark:text-white"></i>,
    subItems: [
      {
        to: "/admin/dashboard/statistiques/clients",
        icon: <i className="text-sm las la-user dark:text-white"></i>,
        label: "Clients",
      },
      {
        to: "/admin/dashboard/statistiques/agents",
        icon: <i className="text-sm las la-hands-helping dark:text-white"></i>,
        label: "Prestataires",
      },
      {
        to: "/admin/dashboard/statistiques/owner",
        icon: <FaHome className="text-sm dark:text-white" />,
        label: "Propriétaires",
      },
    ],
  },
  {
    id: 3,
    title: "Utilisateurs",
    icon: <FaUsersCog className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/users/creators",
        icon: <i className="text-sm las la-blog dark:text-white"></i>,
        label: "Rédacteur web",
      },
      {
        to: "/admin/dashboard/users/owners",
        icon: <FaHome className="text-sm dark:text-white" />,
        label: "Propriétaire",
      },
      {
        to: "/admin/dashboard/users/agents",
        icon: <i className="text-sm las la-hands-helping dark:text-white"></i>,
        label: "Prestataire",
      },
      {
        to: "/admin/dashboard/users/clients",
        icon: <i className="text-sm las la-user dark:text-white"></i>,
        label: "Client",
      },
    ],
  },
  {
    id: 2,
    title: "Espaces",
    icon: <FaHome className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/locations/unapproved",
        icon: <i className="text-sm las la-place-of-worship dark:text-white"></i>,
        label: "Liste des espaces",
      },
      {
        to: "/admin/dashboard/locations/categories",
        icon: <HiOutlineSquare3Stack3D strokeWidth={3} className="w-3 h-3 dark:text-white" />,
        label: "Catégories",
      },
      {
        to: "/admin/dashboard/locations/equipments",
        icon: <FaTools className="text-sm dark:text-white" />,
        label: "Équipements",
      },
      {
        to: "/admin/dashboard/locations/events",
        icon: <i className="text-sm las la-glass-cheers dark:text-white"></i>,
        label: "Évènements",
      },
      {
        to: "/admin/dashboard/locations/atmospheres",
        icon: <i className="text-sm las la-laugh-beam dark:text-white"></i>,
        label: "Ambiances",
      },
      {
        to: "/admin/dashboard/locations/playgrounds",
        icon: <i className="text-sm las la-hand-scissors dark:text-white"></i>,
        label: "Aire de jeux",
      },
      {
        to: "/admin/dashboard/locations/rules",
        icon: <FaExclamationTriangle className="text-sm dark:text-white" />,
        label: "Règles",
      },
    ],
  },

  {
    id: 4,
    title: "Prestations",
    icon: <FaStream className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/prestations",
        icon: <i className="text-sm las la-hands-helping dark:text-white"></i>,
        label: "Prestations",
      },
      {
        to: "/admin/dashboard/prestations/fields",
        icon: <FaStream className="text-sm dark:text-white" />,
        label: "Types de prestation",
      },
    ],
  },

  {
    id: 6,
    title: "Réservations",
    icon: <FaCalendar className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/reservations/tentatives",
        icon: <BsCalendarCheck className="text-sm dark:text-white" />,
        label: "Tentatives",
      },
      {
        to: "/admin/dashboard/reservations/pending",
        icon: <BsCalendarCheck className="text-sm dark:text-white" />,
        label: "En attentes",
      },
      {
        to: "/admin/dashboard/reservations/confirmed",
        icon: <BsCalendarCheck className="text-sm dark:text-white" />,
        label: "En cours",
      },
      {
        to: "/admin/dashboard/reservations/confirmed-to-pay",
        icon: <BsCalendarCheck className="text-sm dark:text-white" />,
        label: "À payé",
      },
      {
        to: "/admin/dashboard/reservations/canceled/owner",
        icon: <BsCalendarPlus className="text-sm dark:text-white" />,
        label: "Demandes annulées",
      },
      {
        to: "/admin/dashboard/reservations/canceled/client",
        icon: <BsCalendarPlus className="text-sm dark:text-white" />,
        label: "Annulées par clients",
      },
    ],
  },
  {
    id: 7,
    title: "Stripe",
    icon: <FaStripeS className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/stripe/connected-accounts",
        icon: <BsCalendarCheck className="text-sm dark:text-white" />,
        label: "Comptes connectés",
      },
      {
        to: "/admin/dashboard/stripe/transactions",
        icon: <MdOutlineSwapHoriz className="text-sm dark:text-white" />,
        label: "Transactions",
      },
      {
        to: "/admin/dashboard/stripe/coupons",
        icon: <FaTicketAlt className="text-sm dark:text-white" />,
        label: "Code promo",
      },
    ],
  },
  {
    id: 12,
    title: "Abonnements",
    icon: <Users className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/abonnements-forfaits",
        icon: <Award className="text-sm dark:text-white" />,
        label: "Offres",
      },
      {
        to: "/admin/dashboard/abonnements-caracteristiques",
        icon: <ListCollapse className="text-sm dark:text-white" />,
        label: "Caractéristiques de l'offre",
      },
      {
        to: "/admin/dashboard/abonnements",
        icon: <UserCheck className="text-sm dark:text-white" />,
        label: "Abonnés ",
      },
    ],
  },

  {
    id: 8,
    title: "Réclamations",
    icon: <FaExclamationTriangle className="text-xl dark:text-white" />,
    to: "/admin/dashboard/reservations-reclamations",
  },
  {
    id: 5,
    title: "Paramètres",
    icon: <FaTools className="text-xl dark:text-white" />,
    subItems: [
      {
        to: "/admin/dashboard/settings/home-videos",
        icon: <RiVideoLine className="text-sm dark:text-white" />,
        label: "Home Top vidéo",
      },
      {
        to: "/admin/dashboard/settings/videos",
        icon: <RiVideoLine className="text-sm dark:text-white" />,
        label: "Home Bottom vidéo",
      },
      {
        to: "/admin/dashboard/settings/slider-videos",
        icon: <RiVideoLine className="text-sm dark:text-white" />,
        label: "Home Slider vidéos",
      },
      {
        to: "/admin/dashboard/settings/home-demo-video",
        icon: <RiVideoLine className="text-sm dark:text-white" />,
        label: "Home Demo vidéo",
      },
      {
        to: "/admin/dashboard/settings/slider",
        icon: <RiSlideshowLine className="text-sm dark:text-white" />,
        label: "Slider Images",
      },
      {
        to: "/admin/dashboard/settings/emails",
        icon: <RiMailLine className="text-sm dark:text-white" />,
        label: "Emails",
      },
      {
        to: "/admin/dashboard/settings/stripe-connect",
        icon: <FaStripeS className="text-sm dark:text-white" />,
        label: "Stripe connect",
      },
      {
        to: "/admin/dashboard/settings/location-prices",
        icon: <IoPricetag className="text-sm dark:text-white" />,
        label: "Prix des espaces",
      },
      {
        to: "/admin/dashboard/settings/mot-de-passe",
        icon: <RiLockPasswordFill className="text-sm dark:text-white" />,
        label: "Mot de passe",
      },
    ],
  },
  {
    id: 11,
    title: "Contacts",
    icon: <FaEnvelopeOpenText className="text-xl dark:text-white" />,
    to: "/admin/dashboard/reclamations",
  },
];
